import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fehlerText } from './components/ui';

/**
 * Unbegrenztes Rückgängig/Wiederholen (UX-Regel „Autosave, unbegrenztes Rückgängig, kein Speichern-Dialog“).
 * Jeder Schritt kennt seine Umkehrung als API-Aufruf. Gilt für die laufende Sitzung im Browser.
 * Bewusst NICHT umkehrbar: Vier-Augen-Freigabe, Korrektur, PDF-Upload, Nutzerverwaltung.
 */
export interface Schritt {
  text: string;
  vor: () => Promise<unknown>;
  zurueck: () => Promise<unknown>;
}

interface UndoKontext {
  /** Führt den Schritt aus und legt ihn auf den Stapel. */
  tue: (s: Schritt) => Promise<void>;
  rueckgaengig: () => Promise<void>;
  wiederholen: () => Promise<void>;
  leeren: () => void;
  naechstesRueckgaengig: string | null;
  naechstesWiederholen: string | null;
  /** Zählt Undo/Redo – Bildschirme laden bei Änderung neu. */
  stand: number;
  meldung: string | null;
  laeuft: boolean;
}

const Ctx = createContext<UndoKontext | null>(null);

// ---------- Ersatz-IDs: wird etwas gelöscht und wiederhergestellt, bekommt es eine neue ID ----------

const alias = new Map<string, string>();

/** Aktuelle ID eines (evtl. wiederhergestellten) Objekts. */
export function aktuell(id: string): string {
  let i = id;
  for (let n = 0; alias.has(i) && n < 1000; n++) i = alias.get(i)!;
  return i;
}

export function ersetzt(alt: string, neu: string): void {
  if (alt !== neu) alias.set(aktuell(alt), neu);
}

export function UndoProvider({ children }: { children: ReactNode }) {
  const zurueckStapel = useRef<Schritt[]>([]);
  const vorStapel = useRef<Schritt[]>([]);
  const [, setVersion] = useState(0);
  const [stand, setStand] = useState(0);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const sperre = useRef(false);
  const neu = () => setVersion((v) => v + 1);

  const tue = useCallback(async (s: Schritt) => {
    await s.vor();
    zurueckStapel.current.push(s);
    vorStapel.current = [];
    setMeldung(null);
    neu();
  }, []);

  const bewege = useCallback(async (von: Schritt[], nach: Schritt[], rueck: boolean) => {
    if (sperre.current) return;
    const s = von.pop();
    if (!s) return;
    sperre.current = true;
    setLaeuft(true);
    try {
      await (rueck ? s.zurueck() : s.vor());
      nach.push(s);
      setMeldung(`${rueck ? 'Rückgängig' : 'Wiederholt'}: ${s.text}`);
    } catch (e) {
      // Schritt nicht mehr anwendbar (z. B. Vorgabe inzwischen freigegeben) – verwerfen und melden
      setMeldung(`Nicht möglich (${s.text}): ${fehlerText(e)}`);
    } finally {
      sperre.current = false;
      setLaeuft(false);
      setStand((x) => x + 1);
      neu();
    }
  }, []);

  const rueckgaengig = useCallback(
    () => bewege(zurueckStapel.current, vorStapel.current, true),
    [bewege],
  );
  const wiederholen = useCallback(
    () => bewege(vorStapel.current, zurueckStapel.current, false),
    [bewege],
  );
  const leeren = useCallback(() => {
    zurueckStapel.current = [];
    vorStapel.current = [];
    alias.clear();
    neu();
  }, []);

  // Strg+Z / Strg+Umschalt+Z / Strg+Y – nicht in Eingabefeldern (dort gilt das Rückgängig des Feldes)
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      if (
        ziel &&
        (ziel.tagName === 'INPUT' || ziel.tagName === 'TEXTAREA' || ziel.isContentEditable)
      )
        return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        void rueckgaengig();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        void wiederholen();
      }
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [rueckgaengig, wiederholen]);

  const wert = useMemo<UndoKontext>(
    () => ({
      tue,
      rueckgaengig,
      wiederholen,
      leeren,
      naechstesRueckgaengig: zurueckStapel.current.at(-1)?.text ?? null,
      naechstesWiederholen: vorStapel.current.at(-1)?.text ?? null,
      stand,
      meldung,
      laeuft,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Stapel liegen in Refs; neu() erzwingt das Neuberechnen
    [
      tue,
      rueckgaengig,
      wiederholen,
      leeren,
      stand,
      meldung,
      laeuft,
      zurueckStapel.current.length,
      vorStapel.current.length,
    ],
  );

  return <Ctx.Provider value={wert}>{children}</Ctx.Provider>;
}

const OHNE: UndoKontext = {
  tue: async (s) => {
    await s.vor();
  },
  rueckgaengig: async () => {},
  wiederholen: async () => {},
  leeren: () => {},
  naechstesRueckgaengig: null,
  naechstesWiederholen: null,
  stand: 0,
  meldung: null,
  laeuft: false,
};

export function useUndo(): UndoKontext {
  return useContext(Ctx) ?? OHNE;
}

/** Knöpfe für die Kopfleiste. */
export function UndoKnoepfe() {
  const u = useUndo();
  return (
    <span className="undo">
      <button
        type="button"
        className="knopf knopf--hell"
        onClick={() => void u.rueckgaengig()}
        disabled={!u.naechstesRueckgaengig || u.laeuft}
        title={
          u.naechstesRueckgaengig
            ? `Rückgängig: ${u.naechstesRueckgaengig} (Strg+Z)`
            : 'Nichts rückgängig zu machen'
        }
      >
        ↶ Rückgängig
      </button>
      <button
        type="button"
        className="knopf knopf--hell"
        onClick={() => void u.wiederholen()}
        disabled={!u.naechstesWiederholen || u.laeuft}
        title={
          u.naechstesWiederholen
            ? `Wiederholen: ${u.naechstesWiederholen} (Strg+Y)`
            : 'Nichts zu wiederholen'
        }
      >
        ↷ Wiederholen
      </button>
      <span className="undo__meldung" role="status" aria-live="polite">
        {u.meldung}
      </span>
    </span>
  );
}
